import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import NumaSelect from "@/components/ui/numa-select";
import NumaMascot from "@/components/NumaMascot";
import { numa as numaApi } from "@/lib/api";
import { useLLM } from "@/contexts/LLMContext";
import { sanitizeHtml } from "@/lib/sanitize";
import AiBadge from "@/components/AiBadge";
import { unit as unitOf } from "@/lib/projectTypes";

/**
 * "Da dove iniziare?" - a conversation, not a dashboard.
 *
 * It used to be a page of explanation with a chat box wedged into a third of
 * it, flanked by cards restating what the chat was about. The work happens in
 * the conversation, so the conversation gets the room; the context the model
 * uses is stated once, compactly, where it can be checked.
 *
 * The position of the new section is asked before the content: an outline for
 * something that follows the methodology and precedes the results is a
 * different outline, and the author knows where it goes before they know what
 * goes in it.
 */

function stripHtml(html) {
  const d = document.createElement("div");
  d.innerHTML = sanitizeHtml(html);
  return d.innerText || "";
}

const NONE = "nessuna";

const OPENING =
  "Dimmi di cosa tratterà la nuova sezione. Ti do una scaletta, l'ordine degli argomenti e le domande a cui rispondere — il testo lo scrivi tu.";

export default function StartingPointView({ thesis, pdfs = [], initialPrompt = "" }) {
  const { ensureKey, reportError } = useLLM();
  const [message, setMessage] = useState(initialPrompt || "");
  const [loading, setLoading] = useState(false);
  const [afterId, setAfterId] = useState(NONE);
  const [beforeId, setBeforeId] = useState(NONE);
  const [chat, setChat] = useState([{ role: "assistant", text: OPENING }]);
  const chatEndRef = useRef(null);

  const unit = unitOf(thesis?.project_type);
  const chapters = useMemo(() => thesis?.chapters || [], [thesis]);
  const written = chapters.filter((c) => stripHtml(c.content).trim());

  // Scroll the message list, not the page, and never on the first render.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [chat, loading]);

  useEffect(() => {
    if (initialPrompt) setMessage(initialPrompt);
  }, [initialPrompt]);

  const send = async () => {
    const text = message.trim();
    if (!text) return;
    if (!(await ensureKey())) return;
    setChat((prev) => [...prev, { role: "user", text }]);
    setMessage("");
    setLoading(true);
    try {
      const conversation = [...chat, { role: "user", text }]
        .filter((m) => m.text !== OPENING)
        .slice(-8);
      const res = await numaApi.startingChat({
        thesis_id: thesis?.thesis_id,
        message: text,
        history: conversation,
        after_chapter_id: afterId === NONE ? "" : afterId,
        before_chapter_id: beforeId === NONE ? "" : beforeId,
      });
      setChat((prev) => [...prev, { role: "assistant", text: res.answer }]);
    } catch (e) {
      reportError(e);
    } finally {
      setLoading(false);
    }
  };

  // Radix refuses an empty item value - it is what clears a Select - so
  // "nowhere in particular" needs a name of its own.
  const options = (exclude) => [
    { value: NONE, label: "Non lo so ancora" },
    ...chapters
      .filter((c) => c.id !== exclude)
      .map((c) => ({ value: c.id, label: c.title || "Senza titolo" })),
  ];

  return (
    <div className="flex h-screen flex-col bg-surface numa-fade-in" data-testid="starting-point-view">
      <header className="shrink-0 border-b border-line px-5 pt-7 sm:px-8 lg:px-12">
        <div className="mx-auto flex max-w-3xl items-start gap-4 pb-5">
          <NumaMascot size={44} className="numa-mark hidden shrink-0 sm:block" variant={6} />
          <div className="min-w-0 flex-1">
            <h1 className="font-serif-display text-2xl font-semibold text-ink">Da dove iniziare?</h1>
            <p className="numa-meta mt-1">
              Scalette e domande guida per una sezione che devi ancora scrivere.
            </p>
          </div>
          <AiBadge
            mode="full"
            className="shrink-0"
            detail="Le sezioni già scritte e i punti chiave delle fonti vengono inviati al modello collegato."
          />
        </div>
      </header>

      {/* Placement: two controls, stated in one line. */}
      <div className="shrink-0 border-b border-line bg-surface-2 px-5 py-3 sm:px-8 lg:px-12">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-x-4 gap-y-2">
          <span className="numa-eyebrow shrink-0">Dove andrà</span>
          <div className="flex items-center gap-1.5">
            <ArrowDown className="h-3 w-3 shrink-0 text-ink-subtle" />
            <span className="text-2xs text-ink-muted">dopo</span>
            <NumaSelect
              testId="start-after"
              aria-label={`Dopo quale ${unit}`}
              value={afterId}
              onValueChange={setAfterId}
              className="h-8 min-w-[9rem] text-[12.5px]"
              options={options(beforeId)}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <ArrowUp className="h-3 w-3 shrink-0 text-ink-subtle" />
            <span className="text-2xs text-ink-muted">prima di</span>
            <NumaSelect
              testId="start-before"
              aria-label={`Prima di quale ${unit}`}
              value={beforeId}
              onValueChange={setBeforeId}
              className="h-8 min-w-[9rem] text-[12.5px]"
              options={options(afterId)}
            />
          </div>
          <span className="numa-meta ml-auto hidden sm:block">
            {written.length
              ? `${written.length} ${written.length === 1 ? "sezione scritta" : "sezioni scritte"} · ${pdfs.length} fonti`
              : `${pdfs.length} fonti caricate`}
          </span>
        </div>
      </div>

      {/* The conversation takes everything that is left. */}
      <div className="numa-chat min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-3xl space-y-4">
          {chat.map((m, idx) => (
            <div
              key={idx}
              className={`flex items-end gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              {m.role === "assistant" && (
                <NumaMascot size={26} className="mb-1 hidden shrink-0 sm:block" variant={idx % 8} />
              )}
              <div
                className={
                  m.role === "user"
                    ? "numa-bubble numa-bubble-user max-w-[82%]"
                    : "numa-bubble numa-bubble-numa max-w-[82%]"
                }
              >
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-end gap-2.5">
              <NumaMascot size={26} className="mb-1 hidden shrink-0 sm:block" variant={2} />
              <div className="numa-bubble numa-bubble-numa inline-flex items-center gap-2 text-ink-muted">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-assistant" />
                Sto preparando la scaletta…
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
      </div>

      <div className="shrink-0 border-t border-line bg-surface px-5 py-4 sm:px-8 lg:px-12">
        <div className="mx-auto flex max-w-3xl items-end gap-3">
          <Textarea
            data-testid="starting-chat-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={`Es. In questo ${unit} voglio mostrare come le PMI hanno adottato gli strumenti digitali…`}
            className="min-h-[58px] resize-none rounded-md border-line focus:border-brand focus:ring-brand/20"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") send();
            }}
          />
          <Button
            data-testid="starting-chat-send"
            onClick={send}
            disabled={loading || !message.trim()}
            className="h-10 shrink-0"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Invia
          </Button>
        </div>
      </div>
    </div>
  );
}
